import { useMediaQuery } from '../../hooks/useMediaQuery';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { type Appointment, type CalendarEvent, type Doctor, type Patient, type HoursRange, ROOM_COLORS } from './types';
import { getReservationFeeState } from './reservationFee';
import { getAppointmentPosition, layoutAppointments, formatTime, getAppointmentBlockColor, getReadableTextColor } from './utils';
import { notifyDragStart, notifyDragEnd, shouldSuppressClick } from './dragClickGuard';
import { getEffectiveHc } from '../../utils/patientHc';
import { useTheme } from '../../theme/ThemeContext';
import { AppointmentHoverDetails } from './AppointmentHoverDetails';
import { formatDate } from '../../utils/dateFormat';

const appointmentBlockStyle: React.CSSProperties = {
  background: '#667eea', color: 'white', padding: '1px 4px', margin: '0', borderRadius: '4px', fontSize: '11px',
  minHeight: '20px', minWidth: '60px', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', cursor: 'pointer', overflow: 'hidden',
  textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box',
};

interface Props {
  day: Date;
  index: number;
  HOUR_HEIGHT: number;
  hoursRangeForWeek: HoursRange;
  timeSlots: string[];
  appointments: Appointment[];
  events: CalendarEvent[];
  patients: Patient[];
  doctors: Doctor[];
  nowTick: number;
  onTimeSlotClick: (day: Date, time: string | null, room?: number) => void;
  onAppointmentClick: (a: Appointment, e: React.MouseEvent) => void;
  onEventClick: (ev: CalendarEvent, e: React.MouseEvent) => void;
  onMoveAppointment?: (id: number, newStart: Date | null, newEnd: Date | null, newRoom?: number) => void;
  onMoveEvent?: (id: number, newStart: Date, newEnd: Date) => void;
  eventRailWidth?: number;
  onResizeStart?: (appointment: Appointment, edge: 'top' | 'bottom', columnIndex: number) => void;
  onResize?: (appointment: Appointment, edge: 'top' | 'bottom', deltaMinutes: number, columnIndex: number) => void;
  onResizeEnd?: (appointment: Appointment, edge: 'top' | 'bottom', deltaMinutes: number, columnIndex: number) => void;
  onEventResizeStart?: (event: CalendarEvent, edge: 'top' | 'bottom', columnIndex: number) => void;
  onEventResize?: (event: CalendarEvent, edge: 'top' | 'bottom', deltaMinutes: number, columnIndex: number) => void;
  onEventResizeEnd?: (event: CalendarEvent, edge: 'top' | 'bottom', deltaMinutes: number, columnIndex: number) => void;
  getProspectEvent?: (eventId: number) => CalendarEvent | undefined;
  room?: number;
  filterDoctorId?: string;
  filterDoctorId2?: string;
  filterRoom?: string;
  onAppointmentContextMenu?: (app: Appointment, e: React.MouseEvent) => void;
  onEventContextMenu?: (ev: CalendarEvent, e: React.MouseEvent) => void;
}

export const DayColumn: React.FC<Props> = ({ day, index, HOUR_HEIGHT, hoursRangeForWeek, timeSlots, appointments, events, patients, doctors, nowTick, onTimeSlotClick, onAppointmentClick, onEventClick, onMoveAppointment, onMoveEvent, eventRailWidth, onResizeStart, onResize, onResizeEnd, onEventResizeStart, onEventResize, onEventResizeEnd, getProspectEvent, room, filterDoctorId, filterDoctorId2, filterRoom, onAppointmentContextMenu, onEventContextMenu }) => {
  const { t } = useTranslation();
  const coarsePointer = useMediaQuery('(pointer: coarse)');
  const { tokens } = useTheme();
  const gridBackground = tokens.calendar.grid;
  const gridLine = tokens.calendar.gridLine;
  const columnBorder = tokens.calendar.divider;
  const todayBorder = tokens.app.primary;
  const gridHeight = (hoursRangeForWeek.end - hoursRangeForWeek.start) * HOUR_HEIGHT;
  const totalHeight = gridHeight;

  const datedAppointments = React.useMemo(() => appointments.filter(a => !!a.startTime), [appointments]);

  const laid = React.useMemo(() => layoutAppointments(datedAppointments), [datedAppointments]);
  const now = new Date(nowTick);
  const nowHour = now.getHours() + now.getMinutes() / 60;
  const showNow = now.toDateString() === day.toDateString() && nowHour >= hoursRangeForWeek.start && nowHour < hoursRangeForWeek.end;
  const nowTop = (nowHour - hoursRangeForWeek.start) * HOUR_HEIGHT;
  const EVENT_RAIL_WIDTH = typeof eventRailWidth === 'number' ? eventRailWidth : 14; // px reserved on the right for events so they don't overlap appointments

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [preview, setPreview] = React.useState<{ top: number; label: string } | null>(null);
  const [resizeState, setResizeState] = React.useState<{ item: Appointment | CalendarEvent; type: 'appointment' | 'event'; edge: 'top' | 'bottom'; startY: number; lastDelta: number } | null>(null);
  const [hoveredAppointment, setHoveredAppointment] = React.useState<{ id: number; anchorRect: DOMRect } | null>(null);
  const suppressClickRef = React.useRef(false);
  const suppressHoverRef = React.useRef(false);

  const handleDragStart = (e: React.DragEvent, payload: { type: 'appointment' | 'event'; id: number; durationMs: number }) => {
    suppressHoverRef.current = true;
    setHoveredAppointment(null);
    notifyDragStart();
    e.dataTransfer.setData('application/json', JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'move';
  };

  const showAppointmentHover = (appointmentId: number, element: HTMLElement) => {
    if (suppressHoverRef.current) return;
    setHoveredAppointment({ id: appointmentId, anchorRect: element.getBoundingClientRect() });
  };

  const hideAppointmentHover = () => setHoveredAppointment(null);

  const handleDragEnd = () => {
    notifyDragEnd();
    window.setTimeout(() => {
      suppressHoverRef.current = false;
    }, 0);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const col = containerRef.current;
    if (!col) return;
    const rect = col.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const pixelsFromTop = Math.max(0, Math.min(y, rect.height));
    const minutesFromWindowStart = (pixelsFromTop / HOUR_HEIGHT) * 60;
    const snappedWinMinutes = Math.round(minutesFromWindowStart / 15) * 15;
    const top = (snappedWinMinutes / 60) * HOUR_HEIGHT;
    const totalMinutesFromMidnight = hoursRangeForWeek.start * 60 + snappedWinMinutes;
    const startHourAbs = Math.floor(totalMinutesFromMidnight / 60);
    const startMinuteAbs = totalMinutesFromMidnight % 60;
    const labelDate = new Date(day);
    labelDate.setHours(startHourAbs, startMinuteAbs, 0, 0);
    setPreview({ top, label: formatTime(labelDate) });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/json');
    if (!raw) return;
    let data: { type: 'appointment' | 'event'; id: number; durationMs: number } | null = null;
    try { data = JSON.parse(raw); } catch { return; }
    if (!data) return;
    const col = containerRef.current;
    if (!col) return;
    const rect = col.getBoundingClientRect();
    const y = e.clientY - rect.top;

    // Check if dropped in stand-by area
    if (y >= gridHeight) {
      if (data.type === 'appointment' && onMoveAppointment) {
        // Clear room assignment when moving to stand-by (pass undefined to clear the room)
        onMoveAppointment(data.id, null, null, undefined);
      }
      setPreview(null);
      return;
    }

    const pixelsFromTop = Math.max(0, Math.min(y, rect.height));
    // Convert to minutes from the start of the visible window, then snap to 15 minutes
    const minutesFromWindowStart = (pixelsFromTop / HOUR_HEIGHT) * 60;
    const snappedWinMinutes = Math.round(minutesFromWindowStart / 15) * 15;
    const totalMinutesFromMidnight = hoursRangeForWeek.start * 60 + snappedWinMinutes;
    const startHourAbs = Math.floor(totalMinutesFromMidnight / 60);
    const startMinuteAbs = totalMinutesFromMidnight % 60;
    const startDate = new Date(day);
    startDate.setHours(startHourAbs, startMinuteAbs, 0, 0);
    const endDate = new Date(startDate.getTime() + data.durationMs);
    if (data.type === 'appointment' && onMoveAppointment) onMoveAppointment(data.id, startDate, endDate, room);
    if (data.type === 'event' && onMoveEvent) onMoveEvent(data.id, startDate, endDate);
    setPreview(null);
  };
  const handleDragLeave = () => setPreview(null);

  const resizeStateRef = React.useRef(resizeState);
  React.useLayoutEffect(() => {
    resizeStateRef.current = resizeState;
  }, [resizeState]);

  React.useEffect(() => {
    if (!resizeState) return;
    const initialStart = new Date(resizeState.item.startTime!);
    const initialEnd = new Date(resizeState.item.endTime!);
    const dayStart = new Date(initialStart);
    dayStart.setHours(hoursRangeForWeek.start, 0, 0, 0);
    const dayEnd = new Date(initialEnd);
    dayEnd.setHours(hoursRangeForWeek.end, 0, 0, 0);
    const initialDurationMinutes = Math.round((initialEnd.getTime() - initialStart.getTime()) / 60000);

    const minDeltaTop = -Math.floor((initialStart.getTime() - dayStart.getTime()) / (15 * 60000)) * 15;
    const maxDeltaTop = initialDurationMinutes - 15;
    const minDeltaBottom = -(initialDurationMinutes - 15);
    const maxDeltaBottom = Math.floor((dayEnd.getTime() - initialEnd.getTime()) / (15 * 60000)) * 15;

    const clampDelta = (delta: number, edge: 'top' | 'bottom') => {
      if (edge === 'top') {
        return Math.min(Math.max(delta, minDeltaTop), maxDeltaTop);
      }
      return Math.min(Math.max(delta, minDeltaBottom), maxDeltaBottom);
    };

    const handleMouseMove = (event: MouseEvent) => {
      const current = resizeStateRef.current;
      if (!current) return;
      const deltaPx = event.clientY - current.startY;
      const rawMinutes = (deltaPx / HOUR_HEIGHT) * 60;
      let snapped = Math.round(rawMinutes / 15) * 15;
      snapped = clampDelta(snapped, current.edge);
      if (snapped === current.lastDelta) return;
      setResizeState(prev => prev ? { ...prev, lastDelta: snapped } : prev);
      if (current.type === 'appointment') {
        onResize?.(current.item as Appointment, current.edge, snapped, index);
      } else {
        onEventResize?.(current.item as CalendarEvent, current.edge, snapped, index);
      }
    };

    const handleMouseUp = () => {
      const current = resizeStateRef.current;
      if (!current) return;
      if (current.type === 'appointment') {
        onResizeEnd?.(current.item as Appointment, current.edge, current.lastDelta, index);
      } else {
        onEventResizeEnd?.(current.item as CalendarEvent, current.edge, current.lastDelta, index);
      }
      setResizeState(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  // Resize handlers intentionally depend on the active resize session and callbacks.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [HOUR_HEIGHT, hoursRangeForWeek.end, hoursRangeForWeek.start, index, onResize, onResizeEnd, onEventResize, onEventResizeEnd, resizeState?.type, resizeState?.item.id]);

  const previewResize = React.useMemo(() => {
    if (!resizeState) return null;
    const start = new Date(resizeState.item.startTime!);
    const end = new Date(resizeState.item.endTime!);
    if (resizeState.edge === 'top') {
      start.setMinutes(start.getMinutes() + resizeState.lastDelta);
      if (start >= end) start.setTime(end.getTime() - 15 * 60000);
    } else {
      end.setMinutes(end.getMinutes() + resizeState.lastDelta);
      if (end <= start) end.setTime(start.getTime() + 15 * 60000);
    }
    const durationMinutes = Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000));
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    const durationLabel = hours > 0 ? `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}` : `${minutes}m`;
    const timeLabel = `${formatTime(start)} - ${formatTime(end)}`;
    const label = `${timeLabel} (${durationLabel})`;
    const top = ((start.getHours() + start.getMinutes() / 60) - hoursRangeForWeek.start) * HOUR_HEIGHT;
    const height = (durationMinutes / 60) * HOUR_HEIGHT;
    const lineTop = resizeState.edge === 'bottom' ? top + height : top;
    const lineTimeLabel = resizeState.edge === 'bottom' ? formatTime(end) : formatTime(start);
    return { itemId: resizeState.item.id, top, height, label, edge: resizeState.edge, start, end, lineTop, lineTimeLabel };
  }, [HOUR_HEIGHT, hoursRangeForWeek.start, resizeState]);

  const handleResizeMouseDown = (event: React.MouseEvent, app: Appointment, edge: 'top' | 'bottom') => {
    event.stopPropagation();
    event.preventDefault();
    hideAppointmentHover();
    suppressClickRef.current = true;
    setResizeState({ item: app, type: 'appointment', edge, startY: event.clientY, lastDelta: 0 });
    onResizeStart?.(app, edge, index);
  };

  const handleEventResizeMouseDown = (event: React.MouseEvent, ev: CalendarEvent, edge: 'top' | 'bottom') => {
    event.stopPropagation();
    event.preventDefault();
    suppressClickRef.current = true;
    setResizeState({ item: ev, type: 'event', edge, startY: event.clientY, lastDelta: 0 });
    onEventResizeStart?.(ev, edge, index);
  };

  const isToday = now.toDateString() === day.toDateString();
  const borderStyle = isToday ? `2px solid ${todayBorder}` : `1px solid ${columnBorder}`;

  return (
    <div style={{
      flex: 1,
      borderLeft: (index === 0 && !isToday) ? 'none' : borderStyle,
      borderRight: isToday ? borderStyle : 'none',
      background: gridBackground,
      position: 'relative',
      minHeight: totalHeight,
      boxSizing: 'border-box'
    }}>
      <div
        data-testid="day-column-grid"
        data-calendar-date={`${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`}
        data-calendar-room={room ?? ''}
        ref={containerRef}
        onClick={(e) => {
          if (suppressClickRef.current || shouldSuppressClick()) {
            suppressClickRef.current = false;
            return;
          }
          const rect = e.currentTarget.getBoundingClientRect();
          const y = e.clientY - rect.top;

          if (y >= gridHeight) {
            onTimeSlotClick(day, null, room);
            return;
          }

          const hourIndex = Math.floor(y / HOUR_HEIGHT);
          const hour = hoursRangeForWeek.start + hourIndex;
          const time = `${hour.toString().padStart(2, '0')}:00`;
          onTimeSlotClick(day, time, room);
        }}
        style={{ position: 'relative', height: totalHeight }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
      >
        {timeSlots.map((t, i) => (
          <div key={t} style={{ position: 'absolute', left: 0, right: 0, top: i * HOUR_HEIGHT, height: HOUR_HEIGHT, borderTop: `1px solid ${gridLine}` }} />
        ))}
        {preview && (
          <>
            <div style={{ position: 'absolute', top: preview.top, left: 0, right: 0, height: 2, background: '#228be6', zIndex: 1000, pointerEvents: 'none', boxShadow: '0 0 0 1px rgba(255,255,255,0.65)' }} />
            <div style={{ position: 'absolute', top: preview.top - 12, left: 4, background: '#228be6', color: '#fff', borderRadius: 4, fontSize: 10, padding: '1px 6px', zIndex: 1001, boxShadow: '0 1px 3px rgba(0,0,0,0.25)', pointerEvents: 'none' }}>
              {preview.label}
            </div>
          </>
        )}
        {previewResize && (
          <>
            <div style={{ position: 'absolute', top: previewResize.lineTop, left: 0, right: 0, height: 2, background: '#10b981', zIndex: 1000, pointerEvents: 'none', boxShadow: '0 0 0 1px rgba(255,255,255,0.65)' }} />
            <div style={{ position: 'absolute', top: previewResize.lineTop - 14, left: 4, background: '#10b981', color: '#fff', borderRadius: 4, fontSize: 10, padding: '1px 6px', zIndex: 1001, boxShadow: '0 1px 3px rgba(0,0,0,0.25)', pointerEvents: 'none' }}>
              {previewResize.lineTimeLabel}
            </div>
          </>
        )}
        {showNow && (
          <>
            <div style={{ position: 'absolute', top: nowTop, left: 0, right: 0, height: 2, background: '#ff4d4f', zIndex: 30 }} />
            <div style={{ position: 'absolute', top: nowTop - 4, left: -3, width: 8, height: 8, background: '#ff4d4f', borderRadius: '50%', zIndex: 31, boxShadow: '0 0 0 2px #fff' }} />
          </>
        )}
        {/* Appointments layer (leaves room on the right for the events rail) */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: EVENT_RAIL_WIDTH, bottom: 0 }}>
        {laid.map(app => {
          const pos = getAppointmentPosition(app, HOUR_HEIGHT, hoursRangeForWeek) as any;
          if (pos.hidden) return null;
          const widthPercent = 100 / app._cols;
          const leftPercent = app._col * widthPercent;
          const patient = patients.find(p => p.id === app.patientId);
          const appointmentPatientName = app.patient?.name?.trim();
          const patientDisplayName = patient?.presentedName || patient?.name || (appointmentPatientName && appointmentPatientName.length > 0 ? appointmentPatientName : t('calendar.unknown'));
          const effectiveHc = getEffectiveHc(patient?.hc ?? app.patient?.hc);
          const hasCriticalAllergy = !!patient?.hasCriticalAllergy;
          const noShowCount = patient?.noShowCount ?? 0;

          const financialSummary = app.financialSummary;
          let hasDebt = typeof patient?.balanceDue === 'number' ? patient.balanceDue > 0.5 : false;
          if (!financialSummary && !hasDebt && patient?.treatments) {
            const totalCost = patient.treatments.reduce((sum, t) => sum + (t.cost || 0), 0);
            const totalPaid = patient.payments
              ? patient.payments.reduce((sum, p) => sum + (p.amount || 0), 0)
              : patient.treatments.reduce((sum, t) => {
                  const paid = t.payments?.reduce((pSum, p) => pSum + (p.amount || 0), 0) || 0;
                  return sum + paid;
                }, 0);
            hasDebt = (totalCost - totalPaid) > 0.5;
          }

          const hasUnpaidInstallments = !financialSummary && Array.isArray(app.treatment?.installments)
            ? app.treatment!.installments!.some(ins => ins && !ins.paid)
            : false;
          const hasPendingPayment = financialSummary
            ? !!financialSummary.hasPendingBalance
            : hasDebt || hasUnpaidInstallments;
          const pendingPaymentTitle = financialSummary
            ? `Past due: Bs ${financialSummary.pastDue.toFixed(2)} | Due today: Bs ${financialSummary.dueToday.toFixed(2)} | Balance: Bs ${financialSummary.patientBalanceDue.toFixed(2)}`
            : t('calendar.hasPendingBalance');
          const nearestDebtDate = financialSummary?.treatments
            .filter(treatment => treatment.totalDue > 0 && treatment.nextDueDate)
            .map(treatment => treatment.nextDueDate!)
            .sort()[0] ?? null;

          const embeddedDoctor = app.doctor
            ? { id: app.doctorId ?? -1, firstName: app.doctor.firstName, lastName: app.doctor.lastName, color: app.doctor.color }
            : undefined;
          const embeddedSecondDoctor = app.secondDoctor
            ? { id: app.secondDoctorId ?? -1, firstName: app.secondDoctor.firstName, lastName: app.secondDoctor.lastName, color: app.secondDoctor.color }
            : undefined;
          const doc = (app.doctorId ? doctors.find(d => d.id === app.doctorId) : undefined) || embeddedDoctor;
          const secondDoc = (app.secondDoctorId ? doctors.find(d => d.id === app.secondDoctorId) : undefined) || embeddedSecondDoctor;
          const linkedTreatmentNames = Array.isArray(app.treatments)
            ? app.treatments.map(treatment => treatment?.name).filter(Boolean)
            : [];
          const reasonLabel = linkedTreatmentNames.length > 0
            ? linkedTreatmentNames.join(', ')
            : app.treatment?.name || app.treatmentTemplate?.name || app.customReason || '';
          const isProspect = !!app.isProspect;
          const reservationFeeState = getReservationFeeState(app);
          const needsHcWarning = isProspect || (!!app.patientId && !effectiveHc);
          const blockColor = getAppointmentBlockColor(app, doctors);
          const start = new Date(app.startTime!); const end = new Date(app.endTime!);
          const isPast = end < new Date();
          const showReservationFeeNotPaid =
            !isPast && reservationFeeState.configured && !reservationFeeState.isPaid;
          const roomColor = app.room ? ROOM_COLORS[app.room] : undefined;
          const isResizingThis = resizeState?.item.id === app.id && resizeState?.type === 'appointment';

          const matchesDoc1 = filterDoctorId && (app.doctorId?.toString() === filterDoctorId || app.secondDoctorId?.toString() === filterDoctorId);
          const matchesDoc2 = filterDoctorId2 && (app.doctorId?.toString() === filterDoctorId2 || app.secondDoctorId?.toString() === filterDoctorId2);
          const hasDocFilter = filterDoctorId || filterDoctorId2;
          const isDimmedByDoc = hasDocFilter && !matchesDoc1 && !matchesDoc2;

          const isDimmed = isDimmedByDoc || (filterRoom && app.room?.toString() !== filterRoom);
          const isNoShow = !!app.isNoShow && !isProspect;
          const isCanceled = !isProspect && ((app.status || '').toLowerCase() === 'canceled' || !!app.canceledAt);
          const isConfirmed = !isProspect && !isNoShow && !isCanceled && (app.status || '').toLowerCase() === 'confirmed';
          const millisecondsUntilStart = start.getTime() - nowTick;
          const isUnconfirmedWithin24Hours = !isConfirmed
            && millisecondsUntilStart > 0
            && millisecondsUntilStart < 24 * 60 * 60 * 1000;
          let adjustedTop = pos.top;
          let adjustedHeight = pos.height;
          if (isResizingThis) {
            const delta = resizeState!.lastDelta;
            const deltaPx = (delta / 60) * HOUR_HEIGHT;
            if (resizeState?.edge === 'top') {
              adjustedTop = pos.top + deltaPx;
              adjustedHeight = Math.max(HOUR_HEIGHT / 4, pos.height - deltaPx);
            } else {
              adjustedHeight = Math.max(HOUR_HEIGHT / 4, pos.height + deltaPx);
            }
          }

          const liveStart = isResizingThis && previewResize ? previewResize.start : start;
          const liveEnd = isResizingThis && previewResize ? previewResize.end : end;
          const adjustedHeightPx = typeof adjustedHeight === 'number' ? adjustedHeight : parseFloat(String(adjustedHeight)) || 0;
          const isTinyAppointment = adjustedHeightPx < 24;
          const isCompactAppointment = adjustedHeightPx < 36;
          const showAppointmentTime = adjustedHeight >= 48;
          let displayColor = isCanceled ? '#fee2e2' : isNoShow ? '#d1d5db' : blockColor;
          if (!isProspect && !isNoShow && !isCanceled && secondDoc && secondDoc.color) {
             displayColor = `linear-gradient(135deg, ${blockColor} 50%, ${secondDoc.color} 50%)`;
          }

          const textColor = isProspect ? '#7c2d12' : (isNoShow || isCanceled) ? '#1f2937' : getReadableTextColor(blockColor);
          const prospectEvent = isProspect && app.prospectEventId != null ? getProspectEvent?.(app.prospectEventId) : undefined;
          const style: React.CSSProperties = {
            ...appointmentBlockStyle,
            position: 'absolute',
            top: adjustedTop,
            height: adjustedHeight,
            left: `${leftPercent}%`,
            width: `calc(${widthPercent}% - 0px)`,
            margin: isTinyAppointment ? '1px' : '2px',
            fontSize: isTinyAppointment ? '10px' : '11px',
            padding: isTinyAppointment ? '1px 3px' : isCompactAppointment ? '2px 4px' : '6px',
            lineHeight: isTinyAppointment ? 1 : 1.2,
            background: displayColor,
            color: textColor,
            whiteSpace: isTinyAppointment ? 'nowrap' : 'normal',
            opacity: isDimmed ? 0.4 : ((isNoShow || isCanceled) ? 0.85 : (isPast ? 0.55 : 1)),
            filter: isDimmed ? 'grayscale(100%)' : (isPast ? 'grayscale(25%)' : 'none'),
            zIndex: isResizingThis ? 30 : (isDimmed ? 5 : 10)
          };
          if (isProspect) {
            style.border = '2px dashed #f59e0b';
            style.boxShadow = '0 0 0 2px rgba(245, 158, 11, 0.2)';
          } else if (isCanceled) {
            style.border = '3px solid #991b1b';
            style.boxShadow = '0 0 0 2px rgba(153, 27, 27, 0.18)';
          } else if (isNoShow) {
            style.border = '2px dashed #6b7280';
          } else {
            style.border = isUnconfirmedWithin24Hours
              ? '3px solid #dc2626'
              : isConfirmed
                ? '2px solid #22c55e'
                : '1px solid #475569';
            const shadows = [];
            if (roomColor) shadows.push(`inset 5px 0 0 ${roomColor}`);
            if (hasCriticalAllergy) shadows.push('0 0 0 2px rgba(239,68,68,0.45)');
            style.boxShadow = shadows.length ? shadows.join(', ') : style.boxShadow;
          }
          return (
            <React.Fragment key={app.id}>
            <div
              data-testid={`appointment-card-${app.id}`}
              onClick={(e) => {
                if (suppressClickRef.current || shouldSuppressClick()) {
                  suppressClickRef.current = false;
                  return;
                }
                hideAppointmentHover();
                if (isProspect && prospectEvent) {
                  onEventClick(prospectEvent, e);
                } else {
                  onAppointmentClick(app, e);
                }
              }}
              onContextMenu={(e) => {
                if (onAppointmentContextMenu) {
                  e.preventDefault();
                  onAppointmentContextMenu(app, e);
                }
              }}
              style={style}
              tabIndex={0}
              aria-describedby={hoveredAppointment?.id === app.id ? `appointment-hover-${app.id}` : undefined}
              onMouseEnter={(event) => { if (!coarsePointer) showAppointmentHover(app.id, event.currentTarget); }}
              onMouseDown={hideAppointmentHover}
              onMouseLeave={hideAppointmentHover}
              onFocus={(event) => showAppointmentHover(app.id, event.currentTarget)}
              onBlur={hideAppointmentHover}
              draggable={!coarsePointer && !isResizingThis && !isProspect && !isCanceled}
              onDragStart={(e) => {
                if (isProspect || isCanceled) return;
                handleDragStart(e, { type: 'appointment', id: app.id, durationMs: new Date(app.endTime!).getTime() - new Date(app.startTime!).getTime() });
              }}
              onDragEnd={handleDragEnd}
            >
              {!coarsePointer && !isProspect && !isCanceled && (
                <>
                  <div onMouseDown={(e) => handleResizeMouseDown(e, app, 'top')} style={{ position: 'absolute', top: -4, left: 4, right: 4, height: 8, cursor: 'ns-resize', zIndex: 40 }} />
                  <div onMouseDown={(e) => handleResizeMouseDown(e, app, 'bottom')} style={{ position: 'absolute', bottom: -4, left: 4, right: 4, height: 8, cursor: 'ns-resize', zIndex: 40 }} />
                </>
              )}
              <div style={{ fontWeight: 600, paddingRight: showReservationFeeNotPaid && isTinyAppointment ? 24 : (!isProspect && app.notes && !isCompactAppointment) ? 24 : 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {isProspect ? (app.prospectName || t('calendar.newPatientIntake')) : (
                  <>
                    {patientDisplayName}
                    {effectiveHc && ` - ${effectiveHc}`}
                  </>
                )}
                {needsHcWarning && !isTinyAppointment ? (
                  <span
                    title={t('calendar.missingHcWarning', 'Missing HC. Set HC or create patient at checkout.')}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f97316',
                      color: '#fff7ed',
                      borderRadius: 10,
                      minWidth: 24,
                      height: 16,
                      fontSize: 9,
                      padding: '0 5px',
                      marginLeft: 6,
                      verticalAlign: 'text-bottom',
                      fontWeight: 800
                    }}
                  >
                    HC
                  </span>
                ) : ''}
                {!isProspect && hasCriticalAllergy && !isTinyAppointment ? ' \u26A0\uFE0F' : ''}
                {!isProspect && showReservationFeeNotPaid && (
                  <span
                    title={t('calendar.lifecycle.reservationPendingAmount', 'Reservation pending: Bs {{amount}}').replace('{{amount}}', reservationFeeState.balance.toFixed(2))}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#dc2626',
                      color: 'white',
                      borderRadius: 10,
                      minWidth: 24,
                      height: 16,
                      fontSize: 9,
                      padding: '0 4px',
                      marginLeft: 6,
                      verticalAlign: 'text-bottom',
                      fontWeight: 800,
                      ...(isTinyAppointment ? { position: 'absolute', top: 2, right: 2, marginLeft: 0 } : {})
                    }}
                  >
                    R!
                  </span>
                )}
                {!isProspect && hasPendingPayment && !isTinyAppointment ? (
                  <span title={pendingPaymentTitle} style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#dc2626',
                    color: 'white',
                    borderRadius: 10,
                    minWidth: 24,
                    height: 16,
                    fontSize: 9,
                    padding: '0 4px',
                    marginLeft: 6,
                    verticalAlign: 'text-bottom',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}>$ {formatDate(nearestDebtDate, '')}</span>
                ) : ''}
                {!isProspect && app.notes && !isCompactAppointment && (
                  <span title={t('calendar.hasNotes', 'Has notes')} style={{ position: 'absolute', top: 4, right: app.status === 'stand-by' ? 24 : 4, fontSize: 14 }}>{'\u26A0\uFE0F'}</span>
                )}
                {!isProspect && app.status === 'stand-by' && !isCompactAppointment && (
                  <span title={t('calendar.standBy', 'Stand-by')} style={{ position: 'absolute', top: 4, right: 4, fontSize: 14 }}>{'\u2753'}</span>
                )}
              </div>
              {!isProspect && app.notes && !isCompactAppointment && (
                <div style={{ fontSize: 14, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>{app.notes}</div>
              )}
              {showAppointmentTime && (
                <div style={{ fontSize: 10 }}>{formatTime(liveStart)} - {formatTime(liveEnd)}</div>
              )}
              {!isProspect && !isCompactAppointment && (
                <div style={{ fontSize: 10, opacity: 0.75 }}>
                  {doc
                    ? `${t('calendar.dr')} ${doc.firstName || doc.lastName || ''}`.trim()
                    : t('calendar.unassignedDoctor', 'No doctor assigned')}
                </div>
              )}
              {isProspect && !isCompactAppointment ? (
                <>
                  {app.prospectContact && (
                    <div style={{ fontSize: 10, marginTop: 4 }}>{t('calendar.contact')}: {app.prospectContact}</div>
                  )}
                  {prospectEvent?.notes && (
                    <div style={{ fontSize: 10, marginTop: 4, opacity: 0.85 }}>{prospectEvent.notes}</div>
                  )}
                </>
              ) : !isProspect && !isCompactAppointment ? (
                <div style={{ fontSize: 10, opacity: 0.85 }}>{reasonLabel}</div>
              ) : null}
              {!isProspect && isNoShow && !isCompactAppointment && (
                <div style={{ fontSize: 10, marginTop: 2, fontWeight: 600 }}>{t('calendar.markedAsNoShow')}</div>
              )}
              {!isProspect && isCanceled && app.cancellationNote && !isCompactAppointment && (
                <div style={{ fontSize: 10, marginTop: 2, fontWeight: 600 }}>{app.cancellationNote}</div>
              )}
              {!isProspect && !isNoShow && !isCanceled && noShowCount > 0 && !isCompactAppointment && (
                <div style={{ fontSize: 10, marginTop: 2 }}>{t('calendar.patientNoShows')}: {noShowCount}</div>
              )}
              {isResizingThis && previewResize && previewResize.itemId === app.id && resizeState?.type === 'appointment' && (
                <div style={{ position: 'absolute', top: resizeState?.edge === 'top' ? -22 : adjustedHeight - 18, left: 0, background: '#111827', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 10, zIndex: 100, whiteSpace: 'nowrap', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                  {previewResize.label}
                </div>
              )}
            </div>
            {hoveredAppointment?.id === app.id && !isResizingThis && (
              <div id={`appointment-hover-${app.id}`}>
                <AppointmentHoverDetails
                  appointment={app}
                  patient={patient}
                  prospectEvent={prospectEvent}
                  anchorRect={hoveredAppointment.anchorRect}
                  t={t}
                  patientDisplayName={patientDisplayName}
                  effectiveHc={effectiveHc || undefined}
                  doctor={doc}
                  secondDoctor={secondDoc}
                  reasonLabel={reasonLabel}
                  hasCriticalAllergy={hasCriticalAllergy}
                  needsHcWarning={needsHcWarning}
                  showReservationFeeNotPaid={showReservationFeeNotPaid}
                  hasPendingPayment={hasPendingPayment}
                  pendingPaymentTitle={pendingPaymentTitle}
                  isNoShow={isNoShow}
                  isCanceled={isCanceled}
                  noShowCount={noShowCount}
                />
              </div>
            )}
            </React.Fragment>
          );
        })}
        </div>
        {/* Events rail on the right */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: EVENT_RAIL_WIDTH, bottom: 0 }}>
        {(() => {
          // 1. Pre-process events
          const processedEvents = events.map((ev) => {
            if (!ev.startTime || !ev.endTime) return null;
            const start = new Date(ev.startTime);
            const end = new Date(ev.endTime);
            let startHour = start.getHours() + start.getMinutes() / 60;
            let endHour = end.getHours() + end.getMinutes() / 60;
            if (endHour <= hoursRangeForWeek.start || startHour >= hoursRangeForWeek.end) return null;
            startHour = Math.max(startHour, hoursRangeForWeek.start);
            endHour = Math.min(endHour, hoursRangeForWeek.end);
            const duration = Math.max(0.25, endHour - startHour);
            const topOffset = (startHour - hoursRangeForWeek.start) * HOUR_HEIGHT;
            const height = Math.max(2, duration * HOUR_HEIGHT - 2);

            const isResizingThis = resizeState?.item.id === ev.id && resizeState?.type === 'event';
            const adjustedTop = isResizingThis && previewResize && resizeState?.edge === 'top' ? previewResize.top : topOffset;
            const adjustedHeight = isResizingThis && previewResize ? previewResize.height : height;

            return {
              ev,
              top: adjustedTop,
              height: adjustedHeight,
              bottom: adjustedTop + adjustedHeight,
              isResizingThis,
              previewResize
            };
          }).filter((e): e is NonNullable<typeof e> => e !== null)
          .sort((a, b) => a.top - b.top);

          // 2. Group overlapping events
          const groups: (typeof processedEvents)[] = [];
          let currentGroup: typeof processedEvents = [];
          let currentGroupEnd = -1;

          for (const item of processedEvents) {
            if (currentGroup.length === 0) {
              currentGroup.push(item);
              currentGroupEnd = item.bottom;
            } else {
              if (item.top < currentGroupEnd - 1) {
                currentGroup.push(item);
                currentGroupEnd = Math.max(currentGroupEnd, item.bottom);
              } else {
                groups.push(currentGroup);
                currentGroup = [item];
                currentGroupEnd = item.bottom;
              }
            }
          }
          if (currentGroup.length > 0) groups.push(currentGroup);

          // 3. Render groups
          return groups.flatMap(group => {
            const columns: (typeof processedEvents)[] = [];
            group.forEach(item => {
              let placed = false;
              for (let i = 0; i < columns.length; i++) {
                const lastInCol = columns[i][columns[i].length - 1];
                if (item.top >= lastInCol.bottom - 1) {
                  columns[i].push(item);
                  (item as any).colIndex = i;
                  placed = true;
                  break;
                }
              }
              if (!placed) {
                columns.push([item]);
                (item as any).colIndex = columns.length - 1;
              }
            });

            const widthPercent = 100 / columns.length;

            return group.map(item => {
              const { ev, top, height, isResizingThis, previewResize } = item;
              const colIndex = (item as any).colIndex;
              const leftPercent = colIndex * widthPercent;

              const doc = ev.doctorId ? doctors.find(d => d.id === ev.doctorId) : undefined;
              const stripeColor = (doc?.color as string | undefined) || '#667eea';
              const isNewPatientIntake = !!ev.isNewPatientIntake;

              let background = `repeating-linear-gradient(45deg, transparent 0 6px, ${stripeColor}33 6px 12px)`;
              let textColor = '#1f2937';

              if (doc) {
                background = stripeColor;
                // Calculate contrast color
                try {
                  const hex = stripeColor.startsWith('#') ? stripeColor.substring(1) : stripeColor;
                  if (hex.length === 6) {
                    const r = parseInt(hex.substring(0, 2), 16);
                    const g = parseInt(hex.substring(2, 4), 16);
                    const b = parseInt(hex.substring(4, 6), 16);
                    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                    textColor = (yiq >= 160) ? '#1f2937' : '#ffffff';
                  } else {
                    textColor = '#ffffff';
                  }
                } catch (e) {
                  textColor = '#ffffff';
                }
              }

              if (isNewPatientIntake) {
                background = '#fde68a';
                textColor = '#1f2937';
              }
              const displayTitle = isNewPatientIntake
                ? (ev.prospectName && ev.prospectName.trim().length > 0 ? `${t('calendar.newPatientPrefix')}${ev.prospectName}` : t('calendar.newPatientIntake'))
                : ev.title;

              const isPast = new Date(ev.endTime!) < new Date();

              return (
                <div
                  key={`ev-${ev.id}`}
                  onClick={(e) => {
                    if (suppressClickRef.current || shouldSuppressClick()) {
                      suppressClickRef.current = false;
                      return;
                    }
                    onEventClick(ev, e);
                  }}
                  onContextMenu={(e) => {
                    if (onEventContextMenu) {
                      e.preventDefault();
                      onEventContextMenu(ev, e);
                    }
                  }}
                  draggable={!coarsePointer}
                  onDragStart={(e) => {
                    console.log('Drag start event', ev.id);
                    handleDragStart(e, { type: 'event', id: ev.id, durationMs: new Date(ev.endTime!).getTime() - new Date(ev.startTime!).getTime() });
                  }}
                  onDragEnd={handleDragEnd}
                  style={{
                    position: 'absolute', top: `${top}px`, height: `${height}px`, left: `${leftPercent}%`, width: `${widthPercent}%`, margin: '0',
                    border: isNewPatientIntake ? '1px solid #f59e0b' : '1px solid #d0d7de', borderRadius: 3, background, color: textColor,
                    boxSizing: 'border-box', overflow: 'visible', zIndex: isResizingThis ? 20 : 12, cursor: 'grab',
                    opacity: isPast ? 0.55 : 1,
                    filter: isPast ? 'grayscale(25%)' : 'none'
                  }} title={displayTitle}>
                  {!doc && (
                    <div style={{ fontSize: 10, fontWeight: 600, padding: '4px 6px' }}>{displayTitle}</div>
                  )}
                  {isNewPatientIntake && ev.prospectContact && (
                    <div style={{ fontSize: 9, padding: '0 6px 4px' }}>{t('calendar.contact')}: {ev.prospectContact}</div>
                  )}

                  {/* Resize handles */}
                  <div
                    onMouseDown={(e) => {
                      if (coarsePointer) return;
                      console.log('Resize mouse down top', ev.id);
                      handleEventResizeMouseDown(e, ev, 'top');
                    }}
                    style={{ position: 'absolute', top: -4, left: -10, right: -10, height: 10, cursor: 'ns-resize', zIndex: 50 }}
                  />
                  <div
                    onMouseDown={(e) => {
                      console.log('Resize mouse down bottom', ev.id);
                      handleEventResizeMouseDown(e, ev, 'bottom');
                    }}
                    style={{ position: 'absolute', bottom: -4, left: -10, right: -10, height: 10, cursor: 'ns-resize', zIndex: 50 }}
                  />

                  {isResizingThis && previewResize && previewResize.itemId === ev.id && (
                    <div style={{
                      position: 'absolute',
                      top: resizeState?.edge === 'top' ? -25 : height - 20,
                      right: '100%',
                      marginRight: 4,
                      background: '#111827',
                      color: '#fff',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      zIndex: 100,
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      {previewResize.label}
                    </div>
                  )}
                </div>
              );
            });
          });
        })()}
        </div>
      </div>
    </div>
  );
};
